import { QuestionnaireTempQuestion, QuestionnaireTempSection } from "../../model";
import { useSelectTheme } from "../../store";
import { PopupSize, PopupTemplate } from "..";
import { Dispatch, SetStateAction, useState } from "react";
import _ from "lodash";

export const PopupMoveQuestionnaireQuestion = (props: {
  tempSectionList: QuestionnaireTempSection[];
  setTempSectionList: Dispatch<SetStateAction<QuestionnaireTempSection[]>>;
  sectionId: string;
  movingQuestion: QuestionnaireTempQuestion | null;
  setMovingQuestion: Dispatch<SetStateAction<QuestionnaireTempQuestion | null>>;
}) => {
  const { color, fontWeight } = useSelectTheme();
  const { tempSectionList, setTempSectionList, sectionId, movingQuestion, setMovingQuestion } =
    props;

  const [targetSectionId, setTargetSectionId] = useState<string>("");

  const moveQuestionToAnotherSection = () => {
    if (targetSectionId === "") return;
    const sourceSectionIndex = tempSectionList.findIndex((section) => section.id === sectionId);
    const sourceSection = _.cloneDeep(tempSectionList[sourceSectionIndex]);
    const targetSectionIndex = tempSectionList.findIndex(
      (section) => section.id === targetSectionId,
    );
    const targetSection = _.cloneDeep(tempSectionList[targetSectionIndex]);
    if (sourceSectionIndex === -1 || targetSectionIndex === -1 || !movingQuestion) return;
    const newTempSectionList = [...tempSectionList];
    newTempSectionList[sourceSectionIndex] = {
      ...sourceSection,
      questionList:
        sourceSection.questionList
          ?.filter((q) => q.id !== movingQuestion.id)
          .map((q, i) => ({ ...q, index: i })) ?? [],
    };
    newTempSectionList[targetSectionIndex] = {
      ...targetSection,
      questionList: [...(targetSection.questionList ?? []), movingQuestion].map((q, i) => ({
        ...q,
        index: i,
      })),
    };
    setTempSectionList(newTempSectionList);
    setMovingQuestion(null);
  };

  const SectionListItem = (props: { section: QuestionnaireTempSection; index: number }) => {
    const { section, index } = props;
    const isSelected = section.id === targetSectionId;
    return (
      <div
        key={section.id}
        onClick={() => setTargetSectionId(section.id)}
        css={{
          width: "100%",
          cursor: "pointer",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          border: "1px solid",
          borderRadius: "5px",
          borderColor: isSelected ? color.border.blue : color.border.default,
          backgroundColor: color.surface.white,
          ":hover": {
            borderColor: color.border.dark,
            backgroundColor: color.surface.hover.hoverWhiteBg,
          },
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span css={{ fontWeight: fontWeight.bold }}>
            {section.legend.length > 0 ? section.legend : "Untitled Section"}
          </span>
          <span css={{ fontWeight: fontWeight.bold }}>{`- ${section.questionList?.length} question${
            section.questionList?.length !== 1 ? "s" : ""
          }`}</span>
        </div>
        <span css={{ fontWeight: fontWeight.bold }}>{`Section ${(section.index ?? 0) + 1} of ${
          tempSectionList.length
        }`}</span>
      </div>
    );
  };

  return (
    <PopupTemplate
      {...{
        title: "Move Question to Another Section",
        description:
          "Select the section where you want to move this question. The question will be removed from its current section and placed in the one you choose.",
        background: true,
        popupSize: PopupSize.Medium,
        handleClosePopup: () => setMovingQuestion(null),
        mainFunction: moveQuestionToAnotherSection,
        buttonContent: "Move Question",
        buttonDisabled: targetSectionId === "",
      }}
    >
      <div
        css={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {tempSectionList
          .filter((section) => section.id !== sectionId)
          .map((section, index) => (
            <SectionListItem {...{ key: section.id, section, index }} />
          ))}
      </div>
    </PopupTemplate>
  );
};
